import { EnvelopeIcon, LinkIcon } from '@heroicons/react/20/solid'
import {
  ArrowDownTrayIcon as ArrowDownTrayIconOutline,
  CubeTransparentIcon
} from '@heroicons/react/24/outline'
import {
  ArrowDownTrayIcon as ArrowDownTrayIconSolid,
  MinusCircleIcon,
  PlusCircleIcon,
  Square3Stack3DIcon,
  UserGroupIcon
} from '@heroicons/react/24/solid'
import { rankItem } from '@tanstack/match-sorter-utils'
import { formatDistance, subSeconds } from 'date-fns'
import { de, enUS } from 'date-fns/locale'
import { motion } from 'framer-motion'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { Tooltip as ReactTooltip } from 'react-tooltip'

import {
  EmailVerificationRequiredModal,
  PublicInvitationModal,
  Seo
} from '../components'
import CSVExport from '../components/actions/csv-export'
import DeleteSpace from '../components/actions/delete-space'
import ResetExpired from '../components/actions/reset-expired'
import SendReminderBulk from '../components/actions/send-reminder-bulk'
import SetSpaceState from '../components/actions/set-space-state'
import ActivityIndicator from '../components/activity-indicator'
import CustomerEffortScore from '../components/customer-effort-score'
import HelpSupportHint from '../components/HelpSupportHint'
import NetworkError from '../components/network-error'
import UsageBanner from '../components/pricing/usage-banner'
import ColumnSettings from '../components/react-table/column-settings'
import OptionsDropdown from '../components/react-table/options-dropdown'
import Table from '../components/react-table/table'
import SampleDataHint from '../components/sample-data/sample-data-hint'
import ReinviteSpaceModal from '../components/shared/match-me-if-you-can/reinvite-space-modal'
import Button from '../components/tailwind/Button'
import MenuButton from '../components/tailwind/MenuButton'
import PageHeading from '../components/tailwind/PageHeading'
import CreateSpaceModal from '../components/talents/create-space-modal'
import CrossmodeSlideover from '../components/talents/CrossmodeSlideover'
import StorePartnerLogoModal from '../components/talents/store-partner-logo-modal'
import { SPACE_ACTION_IDS } from '../constants/space-action-ids'
import trackEvents from '../constants/track-events'
import {
  useAivyContext,
  useLanguageContext,
  usePartnerContext,
  usePaymentContext,
  useProductTourContext,
  useUserContext
} from '../context'
import { useSampleDataDialog } from '../context/sample-data-context'
import { useSelectionGuard, useTalentStorage } from '../features/pricing/hooks'
import {
  LIMIT_DIALOG_ACTIONS,
  usePricingDialog
} from '../features/pricing/pricing-dialog-context'
import { query } from '../graphql'
import { S3_AIVY_LOGO, classNames, uuidSmall } from '../helpers'
import { trackEvent } from '../helpers/analytics'
import {
  FILE_PREFIX,
  buildExportColumns,
  parseFieldDefinitions
} from '../helpers/csv-export-columns'
import { SAMPLE_DATA_AVAILABILITY } from '../helpers/sample-data'
import {
  createAdditionalDataColumns,
  createTalentTableColumns
} from '../helpers/spaces-table-columns'
import { TABLE_IDS } from '../helpers/table-column-settings'
import {
  BULK_CONTINUATION,
  crossmodeUsers,
  resolveBulkContinuation,
  selectionDialogOptions,
  withTalentStorageGuard
} from '../helpers/talent-storage-table'
import { useMount } from '../hooks/use-mount'
import { useSampleDataAvailability } from '../hooks/use-sample-data'
import { useSetState } from '../hooks/use-set-state'
import {
  useSpaceActions,
  withNotAvailableHint
} from '../hooks/use-space-actions'
import { useTableColumnSettings } from '../hooks/use-table-column-settings'
import { useTalentsQuery } from '../hooks/use-talents-query'
import { useUpdateFavoredByUserId } from '../hooks/use-update-favored-by-user-id'

const container = {
  hidden: { opacity: 1, scale: 0 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      delayChildren: 0.3,
      staggerChildren: 0.2
    }
  }
}

const item = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1
  }
}

const I18N = 'pages.talents.'
const locale = { de, en: enUS }

const Talents = () => {
  const { t } = useTranslation()

  const { language } = useLanguageContext()
  const { is_trial, products } = usePaymentContext()
  const { partner } = usePartnerContext()
  const { activeSpace, cognitoUser } = useUserContext()

  const { email_verified } = cognitoUser?.attributes || {}
  const isEmailVerified = email_verified === true || email_verified === 'true'

  const { adminInsights } = useAivyContext()
  const { state: navigationState } = useLocation()

  const [isCustomerEffortScoreVisible, setIsCustomerEffortScoreVisible] =
    useState(false)

  // use ref because more than one request could be in process
  const crossmodeLoading = useRef([])

  // const [guided_steps] = useState(talents_steps)

  const navigate = useNavigate()

  useEffect(() => {
    const storageSearchParams = localStorage.getItem('/talents')

    if (storageSearchParams) {
      navigate({ search: storageSearchParams }, { replace: true })
    }
  }, [navigate])

  const {
    setProductTourState,
    productTourState: { tourActive }
  } = useProductTourContext()

  useMount(() => {
    if (tourActive) {
      setTimeout(() => {
        setProductTourState({ run: true })
      }, 0.4 * 1000)
    }
  })

  const [selectionState, setSelectionState] = useSetState({
    spaces: [],
    career_ids: []
  })

  const [time, setTime] = useState(null)
  const [showCreateSpaceModal, setShowCreateSpaceModal] = useState(false)
  const [showStorePartnerLogo, setShowStorePartnerLogo] = useState(false)
  const [invitationType, setInvitationType] = useState(null)
  const [showReinviteSpaceModal, setShowReinviteSpaceModal] = useState(null)
  const [
    isEmailVerificationRequiredModalVisible,
    setIsEmailVerificationRequiredModalVisible
  ] = useState(false)
  const [isPublicInvitationModalVisible, setIsPublicInvitationModalVisible] =
    useState(false)
  const [crossmode, setCrossmode] = useState(false)
  const [matchBatchIfYouCan, setMatchBatchIfYouCan] = useState({})
  const [resetTableKey, setResetTableKey] = useState(uuidSmall())
  const [spaceForStateCTA, setSpaceForStateCTA] = useState(null)
  const [rerender, setRerender] = useState(uuidSmall())
  // frisch eingeladene Talente leuchten nach dem Einladen kurz in der Tabelle auf
  const [highlightSpaceIds, setHighlightSpaceIds] = useState([])

  useEffect(() => {
    if (!highlightSpaceIds.length) return undefined

    const timeout = setTimeout(() => setHighlightSpaceIds([]), 1000 * 5)
    return () => clearTimeout(timeout)
  }, [highlightSpaceIds])

  const [csvExportModalState, setCsvExportModalState] = useSetState({
    open: false,
    state: null
  })

  const {
    actions: spaceActions,
    displaySendReminder,
    setDisplaySendReminder,
    displaySetSpaceState,
    setDisplaySetSpaceState,
    displayDeleteSpace,
    setDisplayDeleteSpace,
    displayResetExpired,
    setDisplayResetExpired
  } = useSpaceActions({ spaces: selectionState.spaces })

  const { updateFavoredByUserId, FavoredSpaceHint } = useUpdateFavoredByUserId()

  useEffect(() => {
    if (navigationState === null) return

    if (navigationState.inviteTalentpool === true) {
      setTimeout(() => {
        if (!isEmailVerified) {
          setIsEmailVerificationRequiredModalVisible(true)
          return
        }

        setShowCreateSpaceModal(true)
      }, 300)
    }
  }, [isEmailVerified, navigationState])

  useEffect(() => {
    // fetch only if crossmode gets active
    if (!crossmode) return

    // or a career gets added / removed
    if (!selectionState.career_ids.length) return

    const fetchCrossmode = () => {
      crossmodeLoading.current.push('fetching')
      setRerender(uuidSmall())

      query({
        query: 'matchBatchIfYouCan',
        variables: {
          careers: selectionState.career_ids,
          // Gesperrte Talente (Talentspeicher) gehen nie mit
          users: crossmodeUsers(selectionState.spaces)
        }
      }).then((response) => {
        setMatchBatchIfYouCan(JSON.parse(response))
        crossmodeLoading.current.pop()
        setRerender(uuidSmall())
      })
    }

    fetchCrossmode()
  }, [selectionState, crossmode])

  // http://talentsheet.aivy.app/?career_id=5d04ffdd-5826-726c-2c04-f9fd9506a22d&language=de&space_id=6ceb3ace-df06-71fa-28f5-62318a2a2570&user_id=3d0cabab-9861-7942-1f76-a05c7f39faff

  const { status, data, refetch, dataUpdatedAt, fetchStatus } =
    useTalentsQuery()

  // Talentspeicher (nur enforced). pending heißt, die Entitlements eines
  // Plan-Kunden stehen noch nicht fest oder die Liste ist noch nicht gezählt.
  // Bis dahin lädt die Seite weiter, damit keine echten Scores aufblitzen.
  // Gelesen wird nur der Cache, geladen wird über useTalentsQuery.
  const { pending: talentStoragePending } = useTalentStorage()
  const { checkSelection } = useSelectionGuard()
  const { openLimitDialog } = usePricingDialog()

  const { openSampleDataDialog } = useSampleDataDialog()
  const { availability, canAdd } = useSampleDataAvailability({
    spaces: data?.spaces
  })
  const canAddSampleData =
    canAdd && availability === SAMPLE_DATA_AVAILABILITY.NONE

  const fetchingSubtitle = useMemo(() => {
    if (status === 'error') return null

    // data updated at defaults to 0 for an empty cache
    if (!dataUpdatedAt) {
      return t('talents.fetching_subtitle.is_loading')
    }

    if (fetchStatus === 'fetching') {
      return t('talents.fetching_subtitle.is_updating')
    }

    return t('talents.fetching_subtitle.last_updated_at', {
      time, // only used here for the use memo dependencies
      distance_sub_seconds: formatDistance(
        subSeconds(new Date(dataUpdatedAt), 3),
        new Date(),
        {
          locale: locale[language]
        }
      )
    })
  }, [status, dataUpdatedAt, fetchStatus, time, language, t])

  useEffect(() => {
    // update fetching subtitle every minute
    const interval = setInterval(() => setTime(Date.now()), 1000 * 60)

    return () => {
      clearInterval(interval)
    }
  }, [])

  const careers_map = useMemo(() => {
    return new Map([
      ...(data ? data.careers : []).map((career) => [career.id, career]),
      ['TALENTPOOL', { title: t('talents.talentpool_title') }],
      ['ATTRACT', { title: 'Attract' }]
    ])
  }, [data, t])

  const handleNavigate = useCallback(
    (cell) => {
      const { id: space_id } = cell.row.original

      navigate(`/talent/${space_id}`)
    },
    [navigate]
  )

  const partnerFields = useMemo(
    () =>
      parseFieldDefinitions(
        partner?.app_settings?.application_input_additional_fields
      ),
    [partner]
  )

  // Alles, was im Einladungsformular abgefragt wird, als optionale Spalten.
  // Derselbe Katalog wie im CSV-Export, ein Parse-Lauf pro Space.
  const additionalColumns = useMemo(() => {
    if (!data?.spaces?.length) return []

    const { columns: exportColumns, ctx } = buildExportColumns({
      spaces: data.spaces,
      careersMap: careers_map,
      partnerFields,
      t,
      language,
      partnerOriginLabel: t('spaces.actions.csv_export.origin_partner')
    })

    return createAdditionalDataColumns({
      fields: exportColumns.filter(
        ({ id, group }) => group === 'additional' && !id.startsWith(FILE_PREFIX)
      ),
      ctx
    })
  }, [data, careers_map, partnerFields, t, language])

  const columns = useMemo(
    () =>
      createTalentTableColumns({
        adminInsights,
        careers_map,
        data,
        handleNavigate,
        language,
        locale,
        partner,
        t,
        actions: {
          setSpaceForStateCTA: (space) => {
            setSpaceForStateCTA(space)
            trackEvent(trackEvents.PRESS_SPACE_SET_STATE_TALENTTABLE)
          },
          updateFavoredByUserId,
          // Score-Zelle eines gesperrten Talents, statt generatePdf
          openTalentStorageDialog: () =>
            openLimitDialog('talentStorage', {
              action: LIMIT_DIALOG_ACTIONS.INFO,
              source: 'talents_score_cell'
            })
        },
        displayIntegrationFilterOptions: (products || []).includes(
          'INTEGRATIONS'
        ),
        additionalColumns
      }),
    [
      additionalColumns,
      data,
      careers_map,
      handleNavigate,
      adminInsights,
      t,
      partner,
      language,
      updateFavoredByUserId,
      products,
      openLimitDialog
    ]
  )

  const columnSettings = useTableColumnSettings({
    tableId: TABLE_IDS.TALENTS,
    columns
  })

  const globalFilterFn = useCallback(
    (row, columnId, value, addMeta) => {
      let columnValue = row.getValue(columnId)

      if (columnId === 'assessment_id') {
        columnValue = (careers_map.get(columnValue) || {}).title
      }

      const itemRank = rankItem(columnValue, value)

      addMeta({ itemRank })

      return itemRank.passed
    },
    [careers_map]
  )

  const selectionCallback = useCallback(
    (table) => {
      setSelectionState({
        spaces: table
          .getSelectedRowModel()
          .rows.map(({ original, toggleSelected }) => ({
            ...original,
            removeSelected: () => toggleSelected(false)
          }))
      })
    },
    [setSelectionState]
  )

  const dataUpdatedAtIsPastFiveMinutes = useMemo(
    () =>
      time &&
      dataUpdatedAt &&
      Math.floor((new Date() - new Date(dataUpdatedAt)) / 60000) >= 5 &&
      fetchStatus !== 'fetching',
    [dataUpdatedAt, fetchStatus, time]
  )

  // Nach „Gesperrte abwählen" läuft die Massenaktion mit der übrigen Auswahl
  // weiter, sobald die Tabelle die neue Auswahl gemeldet hat
  // (selectionCallback). Ausgelöst wird derselbe Klick wie am Button, also
  // mit Guard und Verfügbarkeit der neuen Auswahl.
  const [bulkContinuation, setBulkContinuation] = useState(null)
  const bulkActionClicksRef = useRef({})
  const selectionSpacesRef = useRef(selectionState.spaces)
  selectionSpacesRef.current = selectionState.spaces

  useEffect(() => {
    if (!bulkContinuation) return
    if (status === 'pending' || talentStoragePending) return

    const decision = resolveBulkContinuation(
      bulkContinuation,
      selectionState.spaces
    )
    if (decision === BULK_CONTINUATION.WAIT) return

    setBulkContinuation(null)
    if (decision === BULK_CONTINUATION.RUN) {
      bulkActionClicksRef.current[bulkContinuation.actionId]?.()
    }
  }, [bulkContinuation, selectionState.spaces, status, talentStoragePending])

  if (status === 'pending' || talentStoragePending) {
    return <ActivityIndicator />
  }

  // rerender is always set, we only need a trigger
  if (status === 'error' || !rerender) {
    return (
      <div className='w-full'>
        <Seo title={t('seo.titles.talents')} />
        <NetworkError />
      </div>
    )
  }

  const handleOnClickInviteSpace = ({ event, type }) => {
    if (!isEmailVerified) {
      setIsEmailVerificationRequiredModalVisible(true)
      return
    }

    setInvitationType(type)
    trackEvent(event)

    if (
      !activeSpace.has_seen_store_partner_logo_hint &&
      partner.logo === S3_AIVY_LOGO
    ) {
      setShowStorePartnerLogo(true)
      return
    }

    setShowCreateSpaceModal(true)
  }

  const closeStorePartnerLogoModal = () => {
    setShowStorePartnerLogo(false)

    if (!isEmailVerified) {
      setTimeout(
        () => setIsEmailVerificationRequiredModalVisible(true),
        0.3 * 1000
      )
      return
    }

    setTimeout(() => setShowCreateSpaceModal(true), 0.3 * 1000)
  }

  const optionsDropdown = [
    data.spaces.length && {
      key: 'csv-export',
      label: t('talents.options.csv_export_title'),
      available: true,
      Icon: ArrowDownTrayIconSolid,
      onClick: () => {
        trackEvent(trackEvents.OPEN_CSV_EXPORT, { state: 'all' })
        setCsvExportModalState({ open: true, state: 'all' })
      }
    },
    canAddSampleData && {
      key: 'add-sample-data',
      label: t('sample_data.add.menu'),
      available: true,
      Icon: PlusCircleIcon,
      onClick: () =>
        openSampleDataDialog({ mode: 'add', source: 'talents_menu' })
    },
    availability === SAMPLE_DATA_AVAILABILITY.PRESENT && {
      key: 'remove-sample-data',
      label: t('sample_data.remove.menu'),
      available: true,
      Icon: MinusCircleIcon,
      onClick: () =>
        openSampleDataDialog({ mode: 'remove', source: 'talents_menu' })
    },
    {
      key: 'recover-talents',
      label: t('talents.options.recover_talents_title'),
      available: true,
      Icon: Square3Stack3DIcon,
      onClick: () => navigate('/recover/talents')
    }
  ].filter((item) => item)

  const pageHeadingParams = {
    title: t('seo.titles.talents'),
    subtitle: fetchingSubtitle,
    withRefetch: dataUpdatedAtIsPastFiveMinutes ? refetch : undefined,
    withActions: (
      <div className='flex flex-wrap items-center gap-x-2'>
        <Button.SecondaryXL
          text={t('career.assessmentsettings.public_link.title')}
          onClick={() => {
            trackEvent('pressedPublicPartnerInvitationButton')

            if (!isEmailVerified) {
              setIsEmailVerificationRequiredModalVisible(true)
              return
            }

            setIsPublicInvitationModalVisible(true)
          }}
        />
        <MenuButton
          cta={t(I18N + 'open_create_space_modal')}
          menuItems={[
            {
              text: t(I18N + 'open_create_space_modal_viaemail'),
              onClick: () =>
                handleOnClickInviteSpace({
                  event: trackEvents.INVITE_VIA_EMAIL,
                  type: 'EMAIL'
                }),
              Icon: EnvelopeIcon
            },
            {
              text: t(I18N + 'open_create_space_modal_vialink'),
              onClick: () =>
                handleOnClickInviteSpace({
                  event: trackEvents.INVITE_VIA_LINK,
                  type: 'LINK'
                }),
              Icon: LinkIcon
            }
          ]}
        />
        {!data.spaces.length && (
          <OptionsDropdown
            menuButtonSize='size-10'
            menuButtonIconSize='size-7'
            options={optionsDropdown}
          />
        )}
      </div>
    )
  }

  if (!data.spaces.length) {
    return (
      <>
        <Seo title={t('seo.titles.talents')} />
        <PageHeading {...pageHeadingParams} />
        {/* z. B. nur Beispielstellen ohne Talente */}
        <SampleDataHint spaces={data.spaces} source='hint_talents' />
        <div className='flex flex-col items-center justify-center px-4'>
          <UserGroupIcon
            className='mx-auto mt-24 h-16 w-16 text-gray-700'
            aria-hidden='true'
          />
          <h3 className='mt-4 text-center text-xl font-medium text-gray-900'>
            {t(I18N + 'title')}
          </h3>
          <p className='mt-4 max-w-xl whitespace-pre-line text-center text-sm text-gray-700'>
            {t(I18N + 'introduction')}
          </p>
        </div>
        <CreateSpaceModal
          data={{ ...data, careers_map }}
          refetchSpaces={refetch}
          open={showCreateSpaceModal}
          invitationType={invitationType}
          closeCreateSpaceModal={() => setShowCreateSpaceModal(false)}
          openCreateSpaceModal={() => setShowCreateSpaceModal(true)}
          onInvited={setHighlightSpaceIds}
        />
        <StorePartnerLogoModal
          open={showStorePartnerLogo}
          setOpen={closeStorePartnerLogoModal}
        />
        <PublicInvitationModal
          open={isPublicInvitationModalVisible}
          setOpen={setIsPublicInvitationModalVisible}
        />

        <EmailVerificationRequiredModal
          open={isEmailVerificationRequiredModalVisible}
          setOpen={setIsEmailVerificationRequiredModalVisible}
          title={t(I18N + 'email_verification_required_modal.title')}
          description={t(
            I18N + 'email_verification_required_modal.description'
          )}
          closeText={t(I18N + 'email_verification_required_modal.close_action')}
          verifyText={t(
            I18N + 'email_verification_required_modal.verify_action'
          )}
          onVerify={() => navigate('/confirm-email')}
        />
      </>
    )
  }

  const displayDefaultActionNotAvailableHint = () => {
    alert(
      t('talents.action_not_availabe.modal_title'),
      [t('talents.action_not_availabe.modal_text')],
      [
        {
          text: t('talents.action_not_availabe.modal_close_action'),
          style: 'cancel'
        }
      ]
    )
  }

  const displayCrossmodeActionNotAvailableHint = () => {
    alert(
      t('talents.crossmode_not_availabe.modal_title'),
      [t('talents.crossmode_not_availabe.modal_text')],
      [
        {
          text: t('talents.crossmode_not_availabe.modal_close_action'),
          style: 'cancel'
        }
      ]
    )
  }

  // Kreuzvergleich, Erinnern und Zurücksetzen nur ohne gesperrte Talente
  // (Talentspeicher). Sonst öffnet der Limit-Dialog mit „Gesperrte abwählen",
  // wenn danach noch Talente übrig sind. Danach läuft die Aktion weiter.
  // Löschen, Status setzen und CSV laufen immer. Ohne Sperren läuft der
  // Klick wie bisher.
  const guardBulkAction = (action) =>
    withTalentStorageGuard(action, {
      spaces: selectionState.spaces,
      checkSelection,
      onBlocked: (result) =>
        openLimitDialog('talentStorage', {
          action: LIMIT_DIALOG_ACTIONS.SELECTION,
          source: `talents_bulk_${String(action.id).replace(/^action-/, '')}`,
          ...selectionDialogOptions({
            spaces: selectionState.spaces,
            result,
            onContinue: () =>
              setBulkContinuation({
                actionId: action.id,
                lockedIds: result.lockedIds,
                fromSpaces: selectionSpacesRef.current
              })
          })
        })
    })

  const displayCrossmodeMax18SpacesHint = (selectedCount) => {
    alert(
      t('talents.crossmode_max_18.modal_title'),
      [
        t('talents.crossmode_max_18.modal_text', {
          selectedCount,
          maxCount: 18
        })
      ],
      [
        {
          text: t('talents.crossmode_max_18.modal_close_action'),
          style: 'cancel'
        }
      ]
    )
  }

  // Buttons der Select-Bar. handleClick ist genau der Klick am Button, mit
  // Talentspeicher-Guard oder dem Hinweis bei nicht verfügbarer Aktion. Die
  // Fortsetzung nach „Gesperrte abwählen" löst ihn erneut aus.
  const bulkActions = [
    {
      id: SPACE_ACTION_IDS.CROSSMODE,
      onClick: () => {
        const { spaces: s } = selectionState
        if (s.length > 18) displayCrossmodeMax18SpacesHint(s.length)
        else setCrossmode(true)
      },
      // this view only makes sense
      available: true,
      handleActionNotAvailable: displayCrossmodeActionNotAvailableHint,
      icons: { outline: CubeTransparentIcon },
      label: t('react_table.select_bar.action_crossmode')
    },
    {
      id: 'csv-export',
      onClick: () => {
        trackEvent(trackEvents.OPEN_CSV_EXPORT, { state: 'selected' })
        setCsvExportModalState({ open: true, state: 'selected' })
      },
      label: t('talents.options.csv_export_title'),
      available: true,
      icons: { outline: ArrowDownTrayIconOutline }
    },
    ...spaceActions.map((action) =>
      withNotAvailableHint(action, displayDefaultActionNotAvailableHint)
    )
  ].map((action) => ({
    ...action,
    handleClick: action.available
      ? guardBulkAction(action).onClick
      : action.handleActionNotAvailable
  }))

  bulkActionClicksRef.current = bulkActions.reduce(
    (clicks, { id, handleClick }) => ({ ...clicks, [id]: handleClick }),
    {}
  )

  return (
    <>
      <Seo title={t('seo.titles.talents')} />
      <PageHeading {...pageHeadingParams} />
      {/* Nur in enforced ab 80 %. Einladen bleibt frei, nur Ergebnisse
          neuer Talente sind gesperrt, bis Platz frei wird */}
      <UsageBanner
        resource='talentStorage'
        source='talents_page'
        className='px-4 pt-4 md:px-6 lg:px-8'
      />
      <SampleDataHint spaces={data.spaces} source='hint_talents' />
      <Table
        key={resetTableKey}
        data={data.spaces}
        columns={columns}
        columnState={columnSettings.columnState}
        globalFilterFn={globalFilterFn}
        initialState={{ sorting: [{ id: 'updatedAt', desc: true }] }}
        withActions={
          <motion.div
            variants={container}
            initial='hidden'
            animate='visible'
            className='mr-8 flex items-center gap-x-2'
          >
            {bulkActions.map(
              ({ id, handleClick, available, icons, label }, index) => {
                const Icon = icons.outline

                return (
                  <div key={index}>
                    <motion.button
                      id={id}
                      data-tooltip-id={id}
                      className={classNames(
                        'group flex h-8 items-center justify-center rounded-full px-1',
                        'cursor-pointer hover:bg-blue-700'
                      )}
                      onClick={handleClick}
                      variants={item}
                    >
                      <Icon
                        className={classNames(
                          'h-6 w-6',
                          available ? 'text-white' : 'text-blue-400'
                        )}
                      />
                    </motion.button>
                    <ReactTooltip
                      id={id}
                      className='rounded-full font-medium'
                      content={label}
                      style={{ backgroundColor: '#182033' }}
                      place='top'
                      variant='info'
                    />
                  </div>
                )
              }
            )}
          </motion.div>
        }
        selectionCallback={selectionCallback}
        disableVirtual={is_trial}
        highlightRowIds={highlightSpaceIds}
        fillHeight
        label={t('seo.titles.talents')}
        displayNumberOfEntries
        withOptions={
          <>
            <ColumnSettings
              tableId={TABLE_IDS.TALENTS}
              settings={columnSettings}
            />
            <OptionsDropdown options={optionsDropdown} />
          </>
        }
      />

      <CrossmodeSlideover
        open={crossmode}
        handleClose={() => setCrossmode(false)}
        {...{
          data,
          setSelectionState,
          crossmodeLoading,
          selectionState,
          matchBatchIfYouCan,
          setShowReinviteSpaceModal,
          careers_map
        }}
      />
      <CreateSpaceModal
        data={{ ...data, careers_map }}
        refetchSpaces={refetch}
        open={showCreateSpaceModal}
        invitationType={invitationType}
        closeCreateSpaceModal={() => setShowCreateSpaceModal(false)}
        openCreateSpaceModal={() => setShowCreateSpaceModal(true)}
        onInvited={setHighlightSpaceIds}
      />
      <ReinviteSpaceModal
        space={selectionState.spaces.find(
          ({ id }) => id === showReinviteSpaceModal
        )}
        updateHandler={refetch}
        open={!!showReinviteSpaceModal}
        setOpen={() => {
          setCrossmode(true)
          setShowReinviteSpaceModal(null)
        }}
      />
      <PublicInvitationModal
        open={isPublicInvitationModalVisible}
        setOpen={setIsPublicInvitationModalVisible}
      />

      <EmailVerificationRequiredModal
        open={isEmailVerificationRequiredModalVisible}
        setOpen={setIsEmailVerificationRequiredModalVisible}
        title={t(I18N + 'email_verification_required_modal.title')}
        description={t(I18N + 'email_verification_required_modal.description')}
        closeText={t(I18N + 'email_verification_required_modal.close_action')}
        verifyText={t(I18N + 'email_verification_required_modal.verify_action')}
        onVerify={() => navigate('/confirm-email')}
      />
      <SetSpaceState
        spaces={selectionState.spaces}
        updateHandler={() => {
          refetch()
          setResetTableKey(uuidSmall())
        }}
        open={displaySetSpaceState}
        setOpen={setDisplaySetSpaceState}
        setIsCustomerEffortScoreVisible={setIsCustomerEffortScoreVisible}
      />
      <SetSpaceState
        spaces={spaceForStateCTA ? [spaceForStateCTA] : []}
        updateHandler={() => {
          refetch()
          setResetTableKey(uuidSmall())
        }}
        open={!!spaceForStateCTA}
        setOpen={() => setSpaceForStateCTA(null)}
        setIsCustomerEffortScoreVisible={setIsCustomerEffortScoreVisible}
      />
      <CustomerEffortScore
        open={isCustomerEffortScoreVisible}
        setOpen={setIsCustomerEffortScoreVisible}
        context='TALENT-STATE'
      />
      <DeleteSpace
        spaces={[...selectionState.spaces]}
        updateHandler={() => {
          setResetTableKey(uuidSmall())
          trackEvent(trackEvents.DELETE_TALENTS)
        }}
        open={displayDeleteSpace}
        setOpen={setDisplayDeleteSpace}
      />
      <SendReminderBulk
        data={{ spaces: selectionState.spaces }}
        careers_map={careers_map}
        updateHandler={() => {
          refetch()
          setResetTableKey(uuidSmall())
        }}
        open={displaySendReminder}
        setOpen={setDisplaySendReminder}
      />
      <ResetExpired
        spaces={selectionState.spaces}
        careers_map={careers_map}
        updateHandler={({ onSuccess }) =>
          refetch().then(() => {
            onSuccess()
            setResetTableKey(uuidSmall())
          })
        }
        open={displayResetExpired}
        setOpen={setDisplayResetExpired}
      />
      <CSVExport
        state={csvExportModalState}
        setState={setCsvExportModalState}
        spaces={
          selectionState.spaces.length ? selectionState.spaces : data.spaces
        }
        careers_map={careers_map}
      />
      <HelpSupportHint />
      <StorePartnerLogoModal
        open={showStorePartnerLogo}
        setOpen={closeStorePartnerLogoModal}
      />
      <FavoredSpaceHint />
    </>
  )
}

export default Talents
